import {Grid} from "@mui/material";
import {useCallback, useEffect, useRef, useState} from "react";
import {useClientLocator} from "../../../client/ApiClientLocator";
import {ButtonBar, DataGridActionBar, useSnackbar} from "@dain/mui-components"
import {FormSessionDto, FormSessionFilterDto} from "../../../client/formsession/FormSessionApiClient";
import {useTranslation} from "react-i18next";
import {CommonTranslKey} from "../../../common/CommonTranslKey";
import {SearchButton} from "../../../common/button/CommonButtons";
import FormSessionGrid from "../FormSessionGrid";
import {PageLoadSize} from "../../../common/pagination/PageableGrid";
import {SortingOrderType} from "../../../client/pagination/SortingFilterDto";
import {PageableResultListDto} from "../../../client/pagination/PageableResultListDto";
import {FormSessionFilterForm} from "./FormSessionFilterForm";

enum SearchPanelState {
    INITIALIZED,
    IDLE,
    SEARCH_IN_PROGRESS
}

export enum FilterSearchType {
    NEW_SEARCH,
    NEXT_PAGE_SEARCH
}

export const DEFAULT_FORM_SESSION_FILTER: FormSessionFilterDto = {
    paging: {
        maxPageSize: PageLoadSize.INITIAL_PAGE_SIZE,
        pageToken: null,
    },
    sorting: {
        sortingCriteria: [
            { property: "createdAt", order: SortingOrderType.DESC },
            { property: "submissionId", order: SortingOrderType.DESC },
        ],
    },
};

export interface FormSessionSearchPanelProps {
    actions?: React.ReactNode;
    onSelectedFormSession: (formSessionDto?: FormSessionDto) => void;
    onFormSessionDoubleClick?: (formSessionId: number) => void;
    actionButtonGridReloadCounter?: number;
}

export default function FormSessionSearchPanel(props: Readonly<FormSessionSearchPanelProps>) {
    const {actions, onSelectedFormSession, onFormSessionDoubleClick} = props;

    const {formSessionClient} = useClientLocator();
    const snackbar = useSnackbar();
    const {t} = useTranslation();

    const [panelStatus, setPanelStatus] = useState<SearchPanelState>(SearchPanelState.INITIALIZED);
    const [formSessionFilter, setFormSessionFilter] = useState<FormSessionFilterDto>(DEFAULT_FORM_SESSION_FILTER);

    const [formSessionResults, setFormSessionResults] = useState<PageableResultListDto<FormSessionDto>>({
        items: [],
        nextPageToken: null,
    });
    const [displayedFormSessionCount, setDisplayedFormSessionCount] = useState<number>(0);

    const [selectedFormSession, setSelectedFormSession] = useState<FormSessionDto>();

    const isInitialized = useRef(false);

    function onFilterChanged(updatedFilter: FormSessionFilterDto) {
        setFormSessionFilter(updatedFilter);
    }

    const updatePaginatedFormSessionResult = (newFetchedFormSessionData: PageableResultListDto<FormSessionDto>, existingFormSessionResults: PageableResultListDto<FormSessionDto>, searchType: FilterSearchType):PageableResultListDto<FormSessionDto> => {
        if (searchType === FilterSearchType.NEW_SEARCH) {
            return newFetchedFormSessionData;
        } else if (searchType === FilterSearchType.NEXT_PAGE_SEARCH) {
            return {
                items: [...existingFormSessionResults.items, ...newFetchedFormSessionData.items],
                nextPageToken: newFetchedFormSessionData.nextPageToken || null
            };
        }

        return {
            items: [],
            nextPageToken: null
        };
    };

    const fetchFormSession = useCallback((filter: FormSessionFilterDto, searchType: FilterSearchType) => {
            setPanelStatus(SearchPanelState.SEARCH_IN_PROGRESS);

            formSessionClient.findByFilter(filter)
                .then((filteredFormSessionPage) => {
                    const updateFormSession = updatePaginatedFormSessionResult(filteredFormSessionPage, formSessionResults, searchType);
                    setFormSessionResults(updateFormSession);
                    setFormSessionFilter(filter);
                })
                .catch((error) => {
                    snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error);
                })
                .finally(() => {
                    setPanelStatus(SearchPanelState.IDLE);
                });
        },
        [formSessionClient, snackbar, formSessionResults, t]
    );

    const countByFilter = useCallback((filter: FormSessionFilterDto) => {
            setPanelStatus(SearchPanelState.SEARCH_IN_PROGRESS);
            return formSessionClient.countByFilter(filter)
                .then((filteredFormSessionCount) => {
                    setDisplayedFormSessionCount(filteredFormSessionCount);
                })
                .catch((error) => snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error))
                .finally(() => setPanelStatus(SearchPanelState.IDLE));
        },
        [formSessionClient, snackbar, t]
    );

    function resetPagingFilter(formSessionFilter: FormSessionFilterDto, defaultFormSessionFilter: FormSessionFilterDto) {
        formSessionFilter.paging = defaultFormSessionFilter.paging;
    }

    const reloadDefaultGrid = useCallback(() => {
        setFormSessionResults({ items: [], nextPageToken: null });
        setDisplayedFormSessionCount(0);

        resetPagingFilter(formSessionFilter, DEFAULT_FORM_SESSION_FILTER);

        fetchFormSession(formSessionFilter, FilterSearchType.NEW_SEARCH);
        countByFilter(formSessionFilter);
    }, [fetchFormSession, countByFilter, formSessionFilter]);

    useEffect(() => {
        if (!isInitialized.current) {
            isInitialized.current = true;
            reloadDefaultGrid();
        }
    }, [reloadDefaultGrid]);

    function onSelectionChange(formSession: FormSessionDto) {
        setSelectedFormSession(formSession);
        onSelectedFormSession(formSession);
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <FormSessionFilterForm filter={formSessionFilter} onChange={onFilterChanged} />
            </Grid>
            <Grid item xs={12}>
                <ButtonBar>
                    <SearchButton onClick={reloadDefaultGrid} />
                </ButtonBar>
            </Grid>
            <Grid item xs={12}>
                <Grid container>
                    {actions && (
                        <Grid item xs={12}>
                            <DataGridActionBar actions={actions} />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        {panelStatus !== SearchPanelState.INITIALIZED && (
                            <FormSessionGrid
                                rowsData={formSessionResults.items}
                                nextPageToken={formSessionResults.nextPageToken}

                                totalDataCount={displayedFormSessionCount}
                                formSessionFilter={formSessionFilter}
                                fetchFormSession={fetchFormSession}

                                selectedFormSessionId={selectedFormSession?.id}
                                onSelectFormSession={onSelectionChange}
                                onFormSessionDoubleClick={onFormSessionDoubleClick}
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}
